/**
 * Générateurs purs des extraits copiables pour connecter un agent MCP à exiPlan.
 *
 * Réécrits pour la conformité au standard MCP (MCP-U1..U3, § mcp/README.md) : plus AUCUN
 * extrait qui affiche un jeton ou un en-tête `Authorization: Bearer …` — l'authentification se
 * fait par OAuth (compte CIAM Exidis), jamais par une clé recopiée dans un fichier de
 * configuration. Plus de passerelle `mcp-remote` : Claude Desktop reçoit désormais le connecteur
 * d'organisation, au même titre que claude.ai.
 *
 * Fonctions pures — aucune dépendance à React ni à l'environnement (`import.meta.env` vit dans
 * `lib/api.js`, pas ici) — pour rester testables isolément et pour que `McpHelpPanel.jsx` soit
 * la SEULE consommatrice de ces gabarits.
 */

/** Port de callback fixe documenté (§ README) : `claude mcp add` en a besoin pour le loopback. */
export const CLAUDE_CODE_CALLBACK_PORT = 9410;

/**
 * `claude mcp add` — client public CIAM (`exiplan-mcp-cli`, redirection loopback), jamais de
 * jeton ni d'en-tête statique. `/mcp` → « Authenticate » ouvre ensuite la connexion CIAM.
 * @param {{ url: string, clientId: string }} params
 */
export function buildClaudeCodeSnippet({ url, clientId }) {
  return (
    `claude mcp add --transport http --scope user exiplan ${url} ` +
    `--client-id ${clientId} --callback-port ${CLAUDE_CODE_CALLBACK_PORT}`
  );
}

/**
 * `.vscode/mcp.json` — même client public que Claude Code, authentification OAuth déclenchée
 * par VS Code au premier appel (aucun secret dans ce fichier, versionnable).
 * @param {{ url: string }} params
 */
export function buildVsCodeMcpJson({ url }) {
  return JSON.stringify({ servers: { exiplan: { type: 'http', url } } }, null, 2);
}

/**
 * Les extraits affichés par `McpHelpPanel` — pas de jeton nulle part.
 * @param {{ url: string, clientId: string }} params
 */
export function buildMcpSnippets({ url, clientId }) {
  return {
    claudeCode: buildClaudeCodeSnippet({ url, clientId }),
    vscode: buildVsCodeMcpJson({ url }),
  };
}

/**
 * Extrait pour un **agent autonome** (standard MCP § 8, MCP-K1..K6 — clé API, pas un client
 * OAuth) : appel direct à `/api/mcp/*`, hors du serveur MCP hébergé. Volontairement distinct des
 * gabarits ci-dessus : ce n'est PAS le flux « humain qui installe un connecteur » que MCP-U3
 * proscrit d'exposer avec un jeton en clair — c'est le seul mode d'authentification d'un agent
 * SANS humain derrière (identité de charge de travail non disponible, § README « Clés API »).
 * Le jeton n'apparaît qu'ici, une fois, dans la réponse de création.
 * @param {{ apiUrl: string, token: string }} params
 */
export function buildApiKeyUsageSnippet({ apiUrl, token }) {
  return `curl -H "Authorization: Bearer ${token}" ${apiUrl}/mcp/whoami`;
}
